import { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    useBoolean,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Alert,
    Button,
    CardBody,
    CardFooter,
    CardHeader,
    Text,
} from 'components/v4';

import { ChakraSpinner } from '@/lib/loading-screen';
import { AltTooltip } from 'components/v4/Tooltip/AltTooltip';
import {
    InstructionMessages,
    PhoneInput,
    QueueStatusMessage,
    TpmCardHeaderContent,
    MfaTeamsPhoneMobileWizard,
    TpmTimeRemaining,
    useTpmContext,
    useTpmState,
    useTpmActions,
} from 'pages/tpm/';

import { CONTINUE, RESET } from '@/store/constants';
import {
    keywords,
    noAssociatedInstructionBody,
    PHONE_INVALID_FORMAT,
    phoneInputStyles,
    associatedInstructionBody,
    MIN_REQUEST_INTERVAL as COUNTDOWN_DURATION,
} from 'pages/tpm/constants';

const TpmAssociatedNumberForm = ({
    hasNoAssociatedAccounts = false,
    ufPhoneNumberInput,
    ufNextButton,
    onClose,
}) => {
    const { tpmNumber } = useTpmContext();
    const state = useTpmState();
    const actions = useTpmActions();
    const [tpmState, setTpmState] = tpmNumber;

    const dispatch = useDispatch();
    const [hoverShow, setHoverShow] = useBoolean();
    const [show, setShow] = useBoolean();
    const { darkMode, colorScheme } = useSelector((s) => s.settings);
    const {
        number: mpn,
        error: tpmError,
        currentStep,
        operatorStatus: oStatus,
        formState,
        lastUpdate,
    } = useSelector((s) => s.service.tpm);

    // Local form state
    const [phoneNumber, setPhoneNumber] = useState(
        state.phoneNumber || tpmState.number || '',
    );
    const [touched, setTouched] = useState(false);
    const [validationError, setValidationError] = useState('');

    // Countdown timer for queue expiration
    const [timeRemaining, setTimeRemaining] = useState(() => {
        if (!lastUpdate) return COUNTDOWN_DURATION;
        const elapsed = Date.now() - lastUpdate;
        return Math.max(0, COUNTDOWN_DURATION - elapsed);
    });

    useEffect(() => {
        if (!lastUpdate) return;
        const interval = setInterval(() => {
            const elapsed = Date.now() - lastUpdate;
            const remaining = Math.max(0, COUNTDOWN_DURATION - elapsed);
            setTimeRemaining(remaining);
        }, 1000);
        return () => clearInterval(interval);
    }, [lastUpdate]);

    // Determine screen based on context state or Redux state
    const screen = state.screen;
    const isQueueExpired = tpmError?.includes('the dynamic search has expired');
    const isDisabled = isQueueExpired || tpmError;
    const isNextButtonDisabled = !phoneNumber || /^\d{1,4}$/.test(phoneNumber); // ignore country code only
    const instructionAlertStatus = hasNoAssociatedAccounts ? 'success' : 'info';
    const formLabelClasses =
        show && isNextButtonDisabled ? '!text-red-500' : '';
    const continueBtnColour =
        colorScheme === 'secondary' ||
        colorScheme === 'green' ||
        colorScheme === 'whatsapp'
            ? 'tertiary'
            : 'secondary';
    const instructionBody = hasNoAssociatedAccounts
        ? noAssociatedInstructionBody
        : associatedInstructionBody;
    const IsQueuingScreen =
        screen === 'LOADING' ||
        (currentStep !== 0 && (oStatus === 'queued' || oStatus === 'pending'));
    const numberFormLabelColor = darkMode ? 'white' : 'gray.700';
    const isFormInvalid = touched && validationError;
    const isContinuingProcess = mpn && currentStep !== 0 && currentStep !== 3;
    const hasError = screen === 'ERROR' || formState === 'error';

    const handleChangeNumber = (phone) => {
        setPhoneNumber(phone);
        setValidationError('');
        setTouched(false);
        // Sync to context
        actions.setPhoneNumber(phone);
        setTpmState({
            status: 'SUBMITTING',
            number: phone,
            error: '',
            touched: false,
        });
    };

    const handleBlur = () => {
        setTouched(true);
        if (!phoneNumber?.match('^[1-9][0-9]+$')) {
            setValidationError(PHONE_INVALID_FORMAT);
        }
    };

    const handleReset = () => {
        setPhoneNumber('');
        setTouched(false);
        setValidationError('');
        actions.reset();
        setTpmState({
            number: '',
            status: 'IDLE',
            error: null,
        });
        dispatch({ type: RESET });
        setShow.off();
    };

    const handleContinueButton = () => {
        dispatch({ type: CONTINUE });
    };

    const handleBack = () => {
        actions.goToScreen('MANAGE_ACCOUNTS');
        handleReset();
    };

    // Error screen
    if (hasError) {
        return (
            <>
                <CardHeader>
                    <Text className="text-lg font-semibold">
                        Operator Validation Alert
                    </Text>
                </CardHeader>

                <CardBody py={0}>
                    <QueueStatusMessage
                        error={state.error || tpmError}
                        darkMode={darkMode}
                    />
                </CardBody>
                <CardFooter alignItems="center" justifyContent="space-between">
                    <Button
                        height="44px"
                        borderRadius="md"
                        onClick={handleReset}>
                        New Number
                    </Button>
                </CardFooter>
            </>
        );
    }

    // Loading/Queue screen
    if (IsQueuingScreen) {
        return (
            <>
                <CardHeader>
                    <Text className="text-lg font-semibold">
                        {!isDisabled
                            ? 'Operator Validation is in progress'
                            : 'Operator Validation Alert'}
                    </Text>
                </CardHeader>
                <CardBody py={0}>
                    <QueueStatusMessage error={tpmError} darkMode={darkMode} />
                </CardBody>

                {(oStatus === 'pending' || oStatus === 'queued') &&
                    !tpmError && (
                        <Flex
                            mt="10px"
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={3}>
                            <ChakraSpinner />
                            <TpmTimeRemaining timeRemaining={timeRemaining} />
                        </Flex>
                    )}

                <CardFooter
                    alignItems="center"
                    justifyContent={
                        tpmError ? 'space-between' : 'space-evenly'
                    }>
                    {tpmError && (
                        <Button
                            height="44px"
                            borderRadius="md"
                            onClick={handleReset}>
                            New Number
                        </Button>
                    )}
                </CardFooter>
            </>
        );
    }

    // Verification screen (MFA code entry)
    if (screen === 'VERIFICATION' || currentStep === 2) {
        return <MfaTeamsPhoneMobileWizard onClose={onClose} />;
    }

    // Number input screen (default)
    return (
        <>
            <CardHeader>
                <TpmCardHeaderContent />
            </CardHeader>

            <CardBody py={0} minW="sm">
                <FormControl
                    data-walkthroughid={ufPhoneNumberInput}
                    isInvalid={isFormInvalid}>
                    <FormLabel
                        htmlFor="activate-number"
                        textAlign="center"
                        me={0}
                        color={numberFormLabelColor}
                        className={formLabelClasses}>
                        Enter Mobile Number
                    </FormLabel>
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        sx={phoneInputStyles(show, isNextButtonDisabled)}>
                        <PhoneInput
                            value={phoneNumber}
                            onChange={handleChangeNumber}
                            onBlur={handleBlur}
                            id="activate-number"
                            name="activate-number"
                        />
                    </Flex>
                    <Box
                        className="-ml-[34px] mt-[6px] flex justify-center pb-[12px] text-xs font-medium leading-none tracking-tight text-[#8e8e8e]"
                        onMouseLeave={setHoverShow.off}>
                        Please ensure you are using{' '}
                        <span
                            className="px-1 hover:cursor-help"
                            onMouseEnter={setHoverShow.on}>
                            E.164 format.
                        </span>
                        <AltTooltip
                            show={hoverShow}
                            label="E.164 is an internationally recognized starndard for formatting telephone numbers. e.g: +61423345678"
                        />
                    </Box>
                </FormControl>

                <Alert mt={5} status={instructionAlertStatus}>
                    <InstructionMessages
                        instructionBody={instructionBody}
                        darkMode={darkMode}
                        keywords={keywords}
                    />
                </Alert>
            </CardBody>

            <CardFooter alignItems="center">
                {!hasNoAssociatedAccounts && (
                    <Button
                        variant="outline"
                        w={{ base: '100%', md: '128px' }}
                        mr={8}
                        onClick={handleBack}>
                        Back
                    </Button>
                )}
                {isContinuingProcess && (
                    <Button
                        w={{ base: '100%', md: '128px' }}
                        mr={8}
                        colorScheme={continueBtnColour}
                        onClick={handleContinueButton}>
                        Continue
                    </Button>
                )}
                <Button
                    type="submit"
                    w={{ base: 'full', md: '130px' }}
                    isDisabled={isNextButtonDisabled}
                    onPointerEnter={setShow.on}
                    onPointerLeave={setShow.off}
                    data-walkthroughid={ufNextButton}>
                    Associate
                </Button>
            </CardFooter>
        </>
    );
};

export default TpmAssociatedNumberForm;
